import React, { useRef, useEffect } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import { useSelector, useDispatch } from 'react-redux';
import { previewImageStorage, getPreviewStorageStateById } from '../../../../ducks/multimedia';
import { useLongPress } from '../../../../hooks/useLongPress';

import {
  ImageBoxContainer,
  MainMarker,
  ImageBorderWrapper,
  Image,
  Controls,
  Rotator,
  Deletor,
  MainSelector
} from './styles';
import defaultImg from '../../../../../assets/img/defaultImg.svg';
import LoadSpinner from './loadspinner';
import Overlay from './overlay';

const type = 'Item';

function Item({
  item,
  onDelete,
  onRotate,
  updateUrl,
  index,
  moveItem,
  onError,
  mainMarker = false,
  mainMarkerTitle = '',
  hasMain = false,
  onMainSelect,
  device,
  postingId,
  itemSelector
}) {
  const dispatch = useDispatch();
  const isMobile = device === 'mobile' || device === 'tablet';
  const ref = useRef(null);
  const hasAttemptedUpload = useRef(false);
  const { isLongPressed, handlers: longPressHandlers } = useLongPress(isMobile);
  const { previewStorageLoading, response, previewStorageError } = useSelector(state =>
    getPreviewStorageStateById(state, item.id)
  );
  const hasError = item.error || previewStorageError;
  const srcUrl =
    item.url || // Viene de la edición del aviso
    item.temporalUrl || // Viene de la creación del aviso
    defaultImg;

  useEffect(() => {
    // Solo se carga si no se ha intentado cargar antes, no tiene una URL, no está en proceso o con error de carga
    if (
      !hasAttemptedUpload.current &&
      !item.temporalUrl &&
      !item.url &&
      !previewStorageLoading &&
      !previewStorageError
    ) {
      const forbiddenErrors = ['errorFormat', 'errorDimensionsMax', 'errorSizeMax'];
      if (!forbiddenErrors.some(error => item.error?.includes(error))) {
        const { file } = item;
        if (file) {
          const formData = new FormData();
          formData.append('file', file, file.name);
          // Disparamos la acción de carga con el ID del item específico
          dispatch(previewImageStorage(formData, item.id, postingId));
          // Marcamos que ya se ha intentado cargar
          hasAttemptedUpload.current = true;
        }
      }
    }
    // Se realiza limpieza al desmontar o cuando cambia el item
    return () => {
      // dispatch(resetPreviewStorage(item.id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  useEffect(() => {
    // Caso de Error en la generación del preview
    if (previewStorageError && typeof onError === 'function') {
      const errorMessage = response?.data?.message || 'errorGeneric';
      onError(item.id, errorMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewStorageError, response]);

  useEffect(() => {
    // Caso Success en la generación del preview
    if (response && typeof updateUrl === 'function') {
      const { temporalUrl, fullName } = response;
      updateUrl(item.id, { temporalUrl, fullName, file: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  const [, drop] = useDrop({
    accept: type,
    hover(dragItem) {
      if (!ref.current) return;
      // Obtiene la posición original y la posición actual donde se está arrastrando:
      const dragIndex = dragItem.index; // Es la posición inicial/origen del elemento que se está arrastrando (de dónde sale)
      const hoverIndex = index; // Es la posición destino donde se está haciendo "hovering" (sobrevolando) con el elemento arrastrado (hacia dónde va)

      // No hace nada si el elemento se arrastra sobre sí mismo:
      if (dragIndex === hoverIndex) return;

      // Reordena los elementos en la colección:
      moveItem(dragIndex, hoverIndex);

      // Actualiza el índice del elemento arrastrado para evitar efectos no deseados
      // si el cursor se mueve a otro elemento drop target:
      dragItem.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag] = useDrag({
    item: { type, id: item.id, index },
    canDrag: !isMobile || isLongPressed,
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  drag(drop(ref));

  const handleRotateClick = e => {
    e.stopPropagation();
    onRotate(item.id);
  };

  const handleDeleteClick = e => {
    e.stopPropagation();
    onDelete(item.id);
  };

  const handleMainSelectClick = e => {
    e.stopPropagation();
    onMainSelect();
  };

  return (
    <ImageBoxContainer
      data-publi={`multimedia-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
        item.order
      }-ImageBoxContainer`}
      ref={ref}
      opacity={isDragging || isLongPressed ? 0.5 : 1}
      className={itemSelector} // Se usa para identificar el elemento y aplicar el max-height desde el hook useSectionsCollapse
      {...longPressHandlers}
    >
      {!previewStorageLoading && hasError && <Overlay item={item} hasError={hasError} />}
      {previewStorageLoading && <LoadSpinner />}
      {srcUrl && (
        <Controls
          data-publi={`multimedia-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
            item.order
          }-controls`}
          device={device}
        >
          {index !== 0 && hasMain && isMobile && !hasError && !previewStorageLoading && (
            <MainSelector
              data-publi={`multimedia-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
                item.order
              }-button-mainSelector`}
              onClick={handleMainSelectClick}
            />
          )}
          {!hasError && !previewStorageLoading && (
            <Rotator
              data-publi={`multimedia-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
                item.order
              }-button-rotator`}
              onClick={handleRotateClick}
            />
          )}
          {!previewStorageLoading && (
            <Deletor
              data-publi={`multimedia-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
                item.order
              }-button-deletor`}
              onClick={handleDeleteClick}
            />
          )}
        </Controls>
      )}
      {mainMarker && srcUrl && index === 0 && (
        <MainMarker
          data-publi={`multimedia-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
            item.order
          }-mainMarker`}
        >
          <div />
          {mainMarkerTitle}
        </MainMarker>
      )}
      {!previewStorageLoading && srcUrl && (
        <>
          <ImageBorderWrapper
            data-publi={`multimedia-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
              item.order
            }-imageBorderWrapper`}
          >
            <Image
              rotationAngle={item.angle}
              src={srcUrl}
              draggable={false}
              data-publi={`multimedia-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
                item.order
              }-editable-img`}
            />
          </ImageBorderWrapper>
        </>
      )}
    </ImageBoxContainer>
  );
}

export { Item };
